import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
if (!BACKEND_URL) {
  console.error("REACT_APP_BACKEND_URL is not set. Please set it in your environment.");
}
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 30000,
  // Auth is carried via httpOnly cookies set by the backend, not via
  // client-readable storage where browsers allow it. Mobile browsers can block
  // cross-site cookies between Vercel/custom domains and Render, so we also
  // keep a bearer-token fallback from the login response.
  withCredentials: true,
});

const ACCESS_KEY = "qm_access_token";
const REFRESH_KEY = "qm_refresh_token";

let isRefreshing = false;
let failedQueue = [];
let authDisabled = false;

let wakeUpDetected = false;
export function isWakeUpSlow() {
  return !wakeUpDetected;
}
export function markWakeUpDone() {
  wakeUpDetected = true;
}

function processQueue(error) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
}

export function pauseAuthRefresh() {
  authDisabled = true;
  processQueue(new Error("Auth refresh paused"));
}

export function resumeAuthRefresh() {
  authDisabled = false;
}

export function saveAuthTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearAuthTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function hasAuthFallback() {
  return !!localStorage.getItem(REFRESH_KEY);
}

export function hasStoredAuthSession() {
  return !!(localStorage.getItem(ACCESS_KEY) || localStorage.getItem(REFRESH_KEY));
}

// Refresh relies on the httpOnly refresh_token cookie; the backend reads it
// automatically and sets fresh cookies on the response. If mobile browsers
// block that cookie, send the fallback refresh token in the request body.
export async function refreshTokens() {
  if (authDisabled) throw new Error("Auth refresh paused");
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  const { data } = await axios.post(`${API}/auth/refresh`, refreshToken ? { refresh_token: refreshToken } : {}, { withCredentials: true });
  saveAuthTokens(data?.token, data?.refresh_token);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    if (authDisabled) {
      return Promise.reject(err);
    }
    if (err.response?.status !== 401 || originalRequest._retry || originalRequest.url?.includes("/auth/refresh") || originalRequest.url?.includes("/auth/login") || originalRequest.url?.includes("/auth/me")) {
      return Promise.reject(err);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => api(originalRequest));
    }
    originalRequest._retry = true;
    isRefreshing = true;
    try {
      await refreshTokens();
      processQueue(null);
      return api(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr);
      clearAuthTokens();
      if (window.location.pathname !== "/login") {
        localStorage.setItem("redirectAfterLogin", `${window.location.pathname}${window.location.search || ""}`);
        window.location.href = "/login";
      }
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

export function formatApiErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return "Soomething went wrong, Please try again later,";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join("");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
